import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CharCounter from '../../../../generic-components/CharCounter';
import { useUploadCommentMutation } from '../../../../hooks/mutations/useUploadCommentMutation';
import { useStore } from '../../../../store';
import saveButtonNotePen from '../../../../assets/svg/saveButtonNotePen.svg';
import Spinner, { SpinnerColor } from '../../../../generic-components/Spinner';
import UpperMessagePopUp from '../UpperMessagePopUp';
import { ERROR_MESSAGE, SUCCESS_MESSAGE, TIMEOUT } from '../../constants';

interface IProps {
  handleOnSuccessUpload?:(value:string, property: string)=>void;
  disabled: boolean;
  saveButtonClassName?: string
  characterCapacity?: number;
  counterClassName?: string;
  textAreaValue: string;
  textAreaPlaceholder: string;
  transactionId: string;
  canAddNote: boolean;
  containerClassName: string;
  textAreaClassName: string;
}

const TextArea = ({
  handleOnSuccessUpload, disabled, transactionId, saveButtonClassName, characterCapacity, counterClassName, containerClassName, canAddNote, textAreaValue, textAreaPlaceholder, textAreaClassName,
}:IProps) => {
  const { t } = useTranslation();
  const [btnText, setBtnText] = useState<string>('');
  const [value, setValue] = useState('');
  const [showPopUp, setShowPopUp] = useState(false);
  const { customerId } = useStore(state => state.user);
  const {
    mutate: uploadComment, isLoading, isError, isSuccess,
  } = useUploadCommentMutation({
    customerId, transactionId, handleOnSuccessUpload, note: value,
  });
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (textAreaValue.length > 0) {
      setBtnText(t('EDIT'));
    }
  }, [textAreaValue]);

  useEffect(() => {
    if (value !== textAreaValue) {
      setBtnText(t('SAVE'));
    } else {
      setBtnText(t('EDIT'));
    }
  }, [value]);

  const changeBtnText = () => {
    if (btnText === t('EDIT')) {
      if (textareaRef.current) {
        textareaRef.current?.focus();
        textareaRef.current.setSelectionRange(
          value.length,
          value.length,
        );
      }
      setBtnText(t('SAVE'));
    } else {
      uploadComment();
      setBtnText(t('EDIT'));
    }
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
  };
  useEffect(() => {
    setValue(textAreaValue);
  }, [transactionId]);

  useEffect(() => {
    if (isError) {
      setShowPopUp(true);
    }
    if (isSuccess) {
      setShowPopUp(true);
      setTimeout(() => {
        setShowPopUp(false);
      }, TIMEOUT);
    }
  }, [isError, isSuccess]);

  return (
    <div className={containerClassName}>
      {showPopUp && (isError || isSuccess) && (
      <UpperMessagePopUp
        handleClose={() => {
          setShowPopUp(false);
        }}
        handleTryAgain={isError ? uploadComment : undefined}
        messageText={t(isError && !isSuccess ? ERROR_MESSAGE : SUCCESS_MESSAGE)}
      />
      )}
      <div className='w-full relative'>
        <textarea
          ref={textareaRef}
          id='transactionDetailsTextArea'
          value={value}
          disabled={disabled}
          className={textAreaClassName}
          maxLength={characterCapacity}
          onChange={handleOnChange}
          placeholder={textAreaPlaceholder}
        />
        {
          canAddNote && (textAreaValue !== '' || value.length > 0) && (
          <button
            id='canAddNoteBtn'
            type='button'
            onClick={changeBtnText}
            className={saveButtonClassName}
          >
            {isLoading
              ? <Spinner width='0.875rem' height='0.875rem' spinnerColor={SpinnerColor.White} />
              : btnText}
            {textAreaValue.length > 0 && textAreaValue === value && !isLoading && btnText !== t('SAVE') && <img id='transactionDetailsSaveNote' className='w-[0.875rem] h-[0.875rem]' src={saveButtonNotePen} alt='saveButtonNotePen' /> }
          </button>
          )
      }
        {characterCapacity && <CharCounter className={counterClassName} currentLength={value.length} maxLength={characterCapacity} />}
      </div>
    </div>
  );
};

export default TextArea;
